import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { NavLink as RouterLink } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Preview, ReceiptLong } from "@mui/icons-material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import TableSkeleton from "../../components/Layout/TableSkeleton";
import Paging from "../../components/Layout/Paging";
import Toast from "../../components/Layout/Toast";
import { selectAuth } from "../auth/authSlice";
import { RoleTypes } from "../auth/types/roleTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import { ClientTypes } from "../auth/types/authType";
import { fetchCompanies, selectSetups } from "../setups/setupSlices";
import { fetchInvoices, selectInvoices } from "./invoiceSlices";
import { InvoiceStatus } from "./types/invoiceTypes";
import { SectionCard } from "./components/SectionCard";

const STATUS_COLOR: Record<
  InvoiceStatus,
  "default" | "warning" | "success" | "error"
> = {
  [InvoiceStatus.unpaid]: "error",
  [InvoiceStatus.partial]: "warning",
  [InvoiceStatus.paid]: "success",
  [InvoiceStatus.cancelled]: "default",
};

export const Invoices = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const {
    invoicesWithCount: { invoices, totalCount },
    loading,
    success,
    error,
  } = useAppSelector(selectInvoices);
  const {
    companiesWithCount: { companies },
  } = useAppSelector(selectSetups);

  const [agentId, setAgentId] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);

  const canManage = isPrivilegedTransaction(
    user?.roles ?? [],
    RoleTypes.ManageInvoicing,
  );

  const agents = companies.filter((c) => c.type === ClientTypes.ForeignAgent);

  useEffect(() => {
    dispatch(fetchCompanies({ searchText: "", skip: 0, take: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchInvoices({
        agentId: agentId || undefined,
        status: (status || undefined) as InvoiceStatus | undefined,
        skip: currentPage * rowsPerPage,
        take: rowsPerPage,
        refreshList: "refresh",
      }),
    );
  }, [dispatch, agentId, status, currentPage, rowsPerPage]);

  return (
    <>
      <Helmet>
        <title>{`Invoices | ${t("platform")}`}</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <SectionCard
          icon={<ReceiptLong />}
          title="Invoices"
          action={
            canManage && (
              <Button
                color="secondary"
                variant="contained"
                component={RouterLink}
                to="/app/invoices/new"
              >
                <Add /> New Invoice
              </Button>
            )
          }
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item md={5} xs={12}>
              <TextField
                id="agentFilter"
                select
                label="Agent"
                value={agentId}
                onChange={(e) => {
                  setCurrentPage(0);
                  setAgentId(e.target.value);
                }}
                variant="outlined"
                size="small"
                fullWidth
              >
                <MenuItem value="">All Agents</MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent._id} value={agent._id}>
                    {agent.displayName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField
                id="statusFilter"
                select
                label={t("status")}
                value={status}
                onChange={(e) => {
                  setCurrentPage(0);
                  setStatus(e.target.value);
                }}
                variant="outlined"
                size="small"
                fullWidth
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.values(InvoiceStatus).map((option) => (
                  <MenuItem key={option} value={option}>
                    {te(option)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </SectionCard>

        {success && <Toast severity="success">{success.message}</Toast>}
        {error && <Toast severity="error">{error.message}</Toast>}

        <TableContainer
          component={Paper}
          elevation={3}
          sx={{ borderRadius: "8px" }}
        >
          <Table size="small" aria-label="invoices table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>S.No</StyledTableCell>
                <StyledTableCell>Invoice No.</StyledTableCell>
                <StyledTableCell>Agent</StyledTableCell>
                <StyledTableCell>Issue Date</StyledTableCell>
                <StyledTableCell>Employees</StyledTableCell>
                <StyledTableCell align="right">Total (USD)</StyledTableCell>
                <StyledTableCell align="right">Paid (USD)</StyledTableCell>
                <StyledTableCell align="right">Balance (USD)</StyledTableCell>
                <StyledTableCell>{t("status")}</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {loading === "pending" ? (
                <TableSkeleton numRows={5} numColumns={9} />
              ) : (
                invoices.map((row, index) => (
                  <StyledTableRow key={row._id}>
                    <StyledTableCell component="th" scope="row">
                      {currentPage * rowsPerPage + index + 1}
                    </StyledTableCell>
                    <StyledTableCell>{row.invoiceNumber}</StyledTableCell>
                    <StyledTableCell>
                      {row.agent?.displayName}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.issueDate &&
                        format(new Date(row.issueDate), "dd-MMM-yyyy")}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.lineItems?.length ?? 0}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {row.totalOwed?.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {row.amountPaid?.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {row.balance?.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        size="small"
                        label={te(row.status as string)}
                        color={STATUS_COLOR[row.status as InvoiceStatus]}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Tooltip title="View Invoice">
                        <IconButton
                          color="primary"
                          component={RouterLink}
                          to={`/app/invoices/${row._id}`}
                          size="large"
                        >
                          <Preview />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Paper elevation={3} sx={{ mt: 1, p: 1.5, borderRadius: "8px" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="h6" color="textPrimary" component="div">
              Number of Invoices: {totalCount}
            </Typography>
            <Paging
              total={totalCount}
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              setRowsPerPage={setRowsPerPage}
              setCurrentPage={setCurrentPage}
            />
          </Stack>
        </Paper>
      </Box>
    </>
  );
};
